import {
  ECardinality,
  EProjectType,
  ESdChangeReason,
  ESymbolCategory,
  IAction,
  IActionParameter,
  ISymbol
} from "@kuika/kuika-cl-model";
import _ from "lodash";
import { IWithSDProps, withSD } from "../../../../shared/hoc/screen-design-consumer";
import { IWithContextProp, withContext } from "../../../../shared/hoc/with-context";
import { PureSdComponent } from "../../../../shared/pure-sd";
import KuikaSelectbox, { KuikaSelectboxOption } from "../../../ui-kit/kuika-selectbox/kuika-selectbox";
import { IActionPanelStatesCanBeUpdatedFromChild } from "./action-panel";
import ActionParameterTitle from "./action-parameter-title";
import { SymbolPicker } from "./symbol-picker";

const TYPE_OPTIONS: KuikaSelectboxOption[] = [
  { key: "default", value: "default", label: "Default", testId: "defaultOption" },
  { key: "info", value: "info", label: "Info", testId: "infoOption" },
  { key: "success", value: "success", label: "Success", testId: "successOption" },
  { key: "warning", value: "warning", label: "Warning", testId: "warningOption" },
  { key: "danger", value: "danger", label: "Danger", testId: "dangerOption" },
  { key: "custom", value: "custom", label: "Custom", testId: "customOption" }
];

const MOBILE_POSITION_OPTIONS: KuikaSelectboxOption[] = [
  { key: "top", value: "top", label: "Top", testId: "TopOption" },
  { key: "center", value: "center", label: "Center", testId: "CenterOption" },
  { key: "bottom", value: "bottom", label: "Bottom", testId: "BottomOption" }
];

const DESKTOP_POSITION_OPTIONS: KuikaSelectboxOption[] = [
  { key: "top-left", value: "top-left", label: "Top Left", testId: "topLeftOption" },
  { key: "top-right", value: "top-right", label: "Top Right", testId: "topRightOption" },
  { key: "top-center", value: "top-center", label: "Top Center", testId: "topCenterOption" },
  { key: "center", value: "center", label: "Center", testId: "centerOption" },
  { key: "bottom-left", value: "bottom-left", label: "Bottom Left", testId: "bottomLeftOption" },
  { key: "bottom-right", value: "bottom-right", label: "Bottom Right", testId: "bottomRightOption" },
  { key: "bottom-center", value: "bottom-center", label: "Bottom Center", testId: "bottomCenterOption" }
];

export interface INotifySymbolsGroupProps {
  action: IAction;
  parameters: IActionParameter[];
  updateValues: (value: IActionPanelStatesCanBeUpdatedFromChild, changeReason?: ESdChangeReason) => void;
}

export interface INotifySymbolsGroupState {}

class NotifySymbolsGroup extends PureSdComponent<
  INotifySymbolsGroupProps & IWithSDProps & IWithContextProp,
  INotifySymbolsGroupState
> {
  constructor(props: INotifySymbolsGroupProps & IWithSDProps & IWithContextProp) {
    super(props);
    this.state = {};
  }

  componentDidMount() {
    // set position to default value

    const position = this.props.parameters.find((p) => p.name === "position");
    if (!position?.symbol?.fixedValue || position.symbol.fixedValue.trim() === "") {
      this.handleSelectValues(this.getDefaultPositionValue(), position);
    }

    const type = this.props.parameters.find((p) => p.name === "type");
    if (!type?.symbol?.fixedValue || type.symbol.fixedValue.trim() === "") {
      this.handleSelectValues("default", type);
    }
  }

  handleSelectValues = (value, parameter, changeReason?: ESdChangeReason) => {
    if (value && Array.isArray(value)) {
      value = value.toString();
    }
    const params = _.cloneDeep(this.props.parameters);
    const _parameter = parameter;
    if (!_parameter.symbol) _parameter.symbol = {} as any;
    _parameter.symbol.fixedValue = value;
    _parameter.symbol.category = ESymbolCategory.fixed;

    if (parameter.name === "type" && value === "custom") {
      let fontSize = params.find((p) => p.name === "fontSize");
      let textColor = params.find((p) => p.name === "textColor");
      let backgroundColor = params.find((p) => p.name === "backgroundColor");

      if (!fontSize) {
        fontSize = this.createFontSizeParameter();
        params.push(fontSize);
      } else if (
        fontSize.symbol.fixedValue === "" ||
        fontSize.symbol.fixedValue === "0" ||
        fontSize.symbol.fixedValue === 0
      ) {
        fontSize.symbol.fixedValue = "14";
      }

      if (!textColor) {
        textColor = this.createTextColorParameter();
        params.push(textColor);
      } else if (textColor.symbol.fixedValue === "") {
        textColor.symbol.fixedValue = "#000000";
      }

      if (!backgroundColor) {
        backgroundColor = this.createBackgroundColorParameter();
        params.push(backgroundColor);
      } else if (backgroundColor.symbol.fixedValue === "") {
        backgroundColor.symbol.fixedValue = "#f0f0f0";
      }
    }

    const valuesToUpdate = {
      actionParameters: params
    } as IActionPanelStatesCanBeUpdatedFromChild;
    valuesToUpdate.actionParameters?.push(_parameter);
    this.props.updateValues(valuesToUpdate, changeReason);
  };

  handleSelectSymbolPickerValue = (parameter: IActionParameter, symbol: ISymbol, changeReason?: ESdChangeReason) => {
    const params = _.cloneDeep(this.props.parameters);
    const _parameter = parameter;
    _parameter.symbol = symbol;
    const valuesToUpdate = {
      actionParameters: params
    } as IActionPanelStatesCanBeUpdatedFromChild;
    valuesToUpdate.actionParameters?.push(_parameter);
    this.props.updateValues(valuesToUpdate, changeReason);
  };

  renderMessage() {
    const message = this.props.parameters.find((p) => p.name === "message");

    return (
      <div>
        <div style={{ marginTop: 12 }} className="kuika_add_actions_properties_name">
          <ActionParameterTitle parameter={{ name: "Message", Typename: "String", cardinality: ECardinality.One }} />
        </div>
        <SymbolPicker
          action={this.props.action}
          changeType="onBlur"
          symbol={message!.symbol}
          valueType={"String"}
          targetCardinality={message!.cardinality}
          onSelectedSymbol={(symbol: ISymbol, changeReason?: ESdChangeReason) =>
            this.handleSelectSymbolPickerValue(message!, symbol, changeReason)
          }
          data_testid="notifyMessage"
          bindingParameter={message}
          bindingLabel="Message"
        />
      </div>
    );
  }

  renderType = () => {
    const type = this.props.parameters.find((p) => p.name === "type");

    return (
      <div>
        <div style={{ marginTop: 12 }} className="kuika_add_actions_properties_name">
          Type
        </div>
        <KuikaSelectbox
          data-testid="typeDropdown"
          onChange={(value: string): void => this.handleSelectValues(value, type)}
          style={{ width: "100%" }}
          className="k_Regular-G1-Left"
          value={type?.symbol.fixedValue ?? null}
          options={TYPE_OPTIONS}
        />
      </div>
    );
  };

  /**
   * Which positions a toast can take is decided by the project, and a project that is neither
   * offered none -- the select was drawn with no rows at all. That stays, as an empty list.
   */
  getPositionOptions = (): KuikaSelectboxOption[] => {
    if (this.props.sd?.project?.projectType === EProjectType.Mobile) return MOBILE_POSITION_OPTIONS;
    if (this.props.sd?.project?.projectType === EProjectType.Desktop) return DESKTOP_POSITION_OPTIONS;
    return [];
  };

  getDefaultPositionValue = () => {
    if (this.props.sd?.project?.projectType === EProjectType.Desktop) {
      return "bottom-right";
    }

    return "top";
  };

  renderPosition = () => {
    const position: IActionParameter | undefined = this.props.parameters.find((p) => p.name === "position");

    return (
      <div>
        <div style={{ marginTop: 12 }} className="kuika_add_actions_properties_name">
          Position
        </div>
        <KuikaSelectbox
          data-testid="positionDropdown"
          onChange={(value: string): void => this.handleSelectValues(value, position)}
          style={{ width: "100%" }}
          className="k_Regular-G1-Left"
          value={position?.symbol.fixedValue ?? null}
          options={this.getPositionOptions()}
        />
      </div>
    );
  };

  renderDuration = () => {
    const duration: IActionParameter | undefined = this.props.parameters.find((p) => p.name === "duration");

    return (
      <div>
        <div style={{ marginTop: 12 }} className="kuika_add_actions_properties_name">
          Duration
        </div>
        <SymbolPicker
          action={this.props.action}
          changeType="onBlur"
          symbol={duration?.symbol}
          valueType={duration?.Typename}
          targetCardinality={duration?.cardinality}
          onSelectedSymbol={(symbol: ISymbol, changeReason?: ESdChangeReason) =>
            this.handleSelectSymbolPickerValue(duration!, symbol, changeReason)
          }
          data_testid="duration"
          bindingParameter={duration}
          bindingLabel="Duration"
        />
      </div>
    );
  };

  renderBackgroundColor = () => {
    let backgroundColor: IActionParameter | undefined = this.props.parameters.find((p) => p.name === "backgroundColor");

    if (!backgroundColor) {
      backgroundColor = {
        name: "backgroundColor",
        cardinality: ECardinality.One,
        Typename: "String",
        symbol: {
          label: "Fixed Value",
          description: "",
          category: ESymbolCategory.fixed,
          fixedValue: "#f0f0f0"
        }
      };
    }

    return (
      <div>
        <div style={{ marginTop: 12 }} className="kuika_add_actions_properties_name">
          Background Color
        </div>
        <SymbolPicker
          action={this.props.action}
          changeType="onBlur"
          symbol={backgroundColor?.symbol}
          valueType={"String"}
          targetCardinality={backgroundColor?.cardinality}
          onSelectedSymbol={(symbol: ISymbol, changeReason?: ESdChangeReason) =>
            this.handleSelectSymbolPickerValue(backgroundColor!, symbol, changeReason)
          }
          data_testid="backgroundColor"
          bindingParameter={backgroundColor}
          bindingLabel="Background Color"
        />
      </div>
    );
  };

  renderTextColor = () => {
    let textColor: IActionParameter | undefined = this.props.parameters.find((p) => p.name === "textColor");

    if (!textColor) {
      textColor = {
        name: "textColor",
        cardinality: ECardinality.One,
        Typename: "String",
        symbol: {
          label: "Fixed Value",
          description: "",
          category: ESymbolCategory.fixed,
          fixedValue: "#000000"
        }
      };
    }

    return (
      <div>
        <div style={{ marginTop: 12 }} className="kuika_add_actions_properties_name">
          Text Color
        </div>
        <SymbolPicker
          action={this.props.action}
          changeType="onBlur"
          symbol={textColor?.symbol}
          valueType={"String"}
          targetCardinality={textColor?.cardinality}
          onSelectedSymbol={(symbol: ISymbol, changeReason?: ESdChangeReason) =>
            this.handleSelectSymbolPickerValue(textColor!, symbol, changeReason)
          }
          data_testid="textColor"
          bindingParameter={textColor}
          bindingLabel="Text Color"
        />
      </div>
    );
  };

  renderFontSize = () => {
    let fontSize: IActionParameter | undefined = this.props.parameters.find((p) => p.name === "fontSize");

    if (!fontSize) {
      fontSize = {
        name: "fontSize",
        cardinality: ECardinality.One,
        Typename: "Int32",
        symbol: {
          label: "Fixed Value",
          description: "",
          category: ESymbolCategory.fixed,
          fixedValue: "14"
        }
      };
    }

    return (
      <div>
        <div style={{ marginTop: 12 }} className="kuika_add_actions_properties_name">
          Font Size
        </div>
        <SymbolPicker
          action={this.props.action}
          changeType="onBlur"
          symbol={fontSize?.symbol}
          valueType={fontSize?.Typename}
          targetCardinality={fontSize?.cardinality}
          onSelectedSymbol={(symbol: ISymbol, changeReason?: ESdChangeReason) =>
            this.handleSelectSymbolPickerValue(fontSize!, symbol, changeReason)
          }
          data_testid="fontSize"
          bindingParameter={fontSize}
          bindingLabel="Font Size"
        />
      </div>
    );
  };

  createFontSizeParameter = () => {
    const fontSize: IActionParameter = {
      name: "fontSize",
      cardinality: ECardinality.One,
      Typename: "Int32",
      symbol: {
        label: "Fixed Value",
        description: "",
        category: ESymbolCategory.fixed,
        fixedValue: "14"
      }
    };

    return fontSize;
  };

  createTextColorParameter = () => {
    const textColor: IActionParameter = {
      name: "textColor",
      cardinality: ECardinality.One,
      Typename: "String",
      symbol: {
        label: "Fixed Value",
        description: "",
        category: ESymbolCategory.fixed,
        fixedValue: "#000000"
      }
    };

    return textColor;
  };

  createBackgroundColorParameter = () => {
    const backgroundColor: IActionParameter = {
      name: "backgroundColor",
      cardinality: ECardinality.One,
      Typename: "String",
      symbol: {
        label: "Fixed Value",
        description: "",
        category: ESymbolCategory.fixed,
        fixedValue: "#f0f0f0"
      }
    };

    return backgroundColor;
  };

  render() {
    const isCustom: boolean = this.props.parameters.find((p) => p.name === "type")?.symbol?.fixedValue === "custom";

    return (
      <div className="kuika_property-grid-column">
        {this.renderMessage()}
        {this.renderPosition()}
        {this.renderDuration()}
        {this.renderType()}
        {isCustom && this.renderBackgroundColor()}
        {isCustom && this.renderTextColor()}
        {isCustom && this.renderFontSize()}
      </div>
    );
  }

  checkNeedRender = (changeReason: ESdChangeReason): boolean => {
    return (
      changeReason === ESdChangeReason.DiActionUpdated ||
      changeReason === ESdChangeReason.ChangeSelectedDiByCanvasFrame ||
      changeReason === ESdChangeReason.ChangeSelectedDiByTools ||
      changeReason === ESdChangeReason.DragDropDi ||
      changeReason === ESdChangeReason.DragDropBlock ||
      changeReason === ESdChangeReason.WFStepMappingChange
    );
  };
}

const notifySymbolsGroup = withContext<INotifySymbolsGroupProps>(withSD<INotifySymbolsGroupProps>(NotifySymbolsGroup));
export { notifySymbolsGroup as NotifySymbolsGroup };
